// src/constants/buildPages/overrides.ts

import {
  withBet,
  withBetNoLeadSpace,
  type SeoService,
  type PageOverrides,
  type ReviewItem,
  type SectionSixOverrides,
  SECTION_FIVE_DEFAULT_IMG,
  SectionFourSEO,
  SEO_SERVICE_EVENT,
  SEO_SERVICE_HENNA,
  SEO_SERVICE_BAR_BAT,
  SEO_SERVICE_WEDDING,
  SECTION_THREE_DEFAULT_ALBUM,
  InternalLink,
  serviceByType,
} from '../core';
import { buildPlanningSectionForService } from '../event-planning';
import type { I18nConstants } from '@/i18n/locales/i18n.factory';
import { globalTypePath, REGIONS, typeLocationPath, type Region, type SeoCity } from '../geo';

/** תיאור עשיר לפי סוג שירות + עיר (לטיזר/פסקת הובלה בסקשן 5) – מובייל */
const EVENT_TYPE_DESCRIPTION = (
  i18n: I18nConstants,
  service: SeoService,
  city: SeoCity
): string => i18n.EVENT_TYPE_DESCRIPTION_COPY[service].mobile(city);

const EVENT_TYPE_DESCRIPTION_DESKTOP = (
  i18n: I18nConstants,
  service: SeoService,
  city: SeoCity
): string => i18n.EVENT_TYPE_DESCRIPTION_COPY[service].desktop(city);

/** כותרות H3 לגלריה (Section 3) */
const buildGalleryHeadings = (
  i18n: I18nConstants,
  service: SeoService,
  keyword: string,
  city: SeoCity
): string[] => {
  const builder = i18n.GALLERY_HEADINGS_COPY[service];
  return builder ? builder(keyword, city) : [];
};

function buildSection4SEO(
  i18n: I18nConstants,
  service: SeoService,
  keyword: string,
  city: SeoCity
): SectionFourSEO {
  return i18n.buildSection4SeoCopy(service, keyword, city);
}

const locSuffix = (i18n: I18nConstants, loc: string) => {
  const clean = String(loc ?? '').trim();
  if (!clean) return '';
  if (i18n.HTML_LANG === 'he') return ` ${withBetNoLeadSpace(clean as any)}`;

  const translated = i18n.translateLocationLabel(clean);
  if (i18n.HTML_LANG === 'fr') return ` à ${translated}`;
  if (i18n.HTML_LANG === 'ru') return ` в ${translated}`;

  return ` in ${translated}`;
};

/** כותרות קבוצות בסקשן 4 (יצירת קשר/אירועים/שירותים/קישורים) */
const buildSection4GroupTitles = (
  i18n: I18nConstants,
  service: SeoService,
  keyword: string,
  city: SeoCity
) => {
  const builder = i18n.SECTION4_GROUP_TITLES_COPY[service];

  return builder
    ? builder(keyword, city)
    : {
        contact: `יצירת קשר עם ${i18n.BRAND_TITLE_HE}`,
        events: i18n.HTML_LANG === 'he'
        ? `קטגוריות אירועים ${withBet(city)}`
        : `Event categories in ${String(city)}`,
      
      services: i18n.HTML_LANG === 'he'
        ? `שירותים משלימים ${withBet(city)}`
        : `Additional services in ${String(city)}`,
      
      links: i18n.HTML_LANG === 'he'
        ? `עמודים נוספים ל${keyword}${withBet(city)}`
        : `Related pages for ${keyword} in ${String(city)}`,
      };
};

/** כותרות H3 לסקשן 5 (בלוג/תוכן ארוך בתוך העמוד) */
const buildSection5Headings = (
  i18n: I18nConstants,
  service: SeoService,
  keyword: string,
  city: SeoCity
): string[] => {
  const hasCity = Boolean(city && String(city).trim() !== '');
  const builder = i18n.SECTION5_HEADINGS_COPY[service];
  return builder ? builder(keyword, city, hasCity) : [];
};

// ============================================================================
// Types
// ============================================================================
type LinkItem = { label: string; href: string; title?: string };

export type BuildOverridesOptions = {
  extraSubtitle?: string;
  section6Reviews?: ReviewItem[];
  section6ShowAggregate?: boolean;

  faqsOverride?: PageOverrides['section5'] extends { faqs?: infer T } ? T : never;

  section5Links?: LinkItem[];
  section5LinksHeading?: string;

  section6Links?: LinkItem[];
  section6LinksHeading?: string;
};

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

type OverrideKind =
  | { kind: 'home' }
  | {
      kind: 'target';
      service: SeoService;
      keyword: string;
      // '' (global) OR any label (city/region)
      locationLabel: string;
    };

// ============================================================================
// Small utilities
// ============================================================================
function deepMerge<T extends object>(base: T, ...partials: DeepPartial<T>[]): T {
  const out: any = Array.isArray(base) ? [...(base as any)] : { ...base };

  for (const p of partials) {
    if (!p) continue;

    for (const [k, v] of Object.entries(p)) {
      if (v === undefined) continue;

      const cur = out[k];
      if (Array.isArray(v)) out[k] = v.slice();
      else if (v && typeof v === 'object') out[k] = deepMerge(cur && typeof cur === 'object' ? cur : {}, v as any);
      else out[k] = v;
    }
  }

  return out;
}

const normalizeLabel = (s: string) => String(s ?? '').trim();
const asSeoCityUnsafe = (label: string): SeoCity => (label as any);
const relUrlWithSlash = (path: string) => (path.endsWith('/') ? path : `${path}/`);

function buildSection4Block(
  i18n: I18nConstants,
  service: SeoService,
  keyword: string,
  locationLabel: string
) {
  const loc = locationLabel.trim();

  const { description, whatsappPrefill, linksHeading } =
    i18n.buildSection4BlockCopy(keyword, loc);

  return {
    title: i18n.SECTION_FOUR_TITLE,
    description,
    whatsappPrefill,
    groupTitles: buildSection4GroupTitles(
      i18n,
      service,
      keyword,
      loc as any
    ),
    linksHeading,
    seo: buildSection4SEO(i18n, service, keyword, loc as any),
  };
}

function buildSection6(
  i18n: I18nConstants,
  keyword: string,
  locationLabel: string,
  current?: SectionSixOverrides,
  opts: {
    reviews?: ReviewItem[];
    showAggregate?: boolean;
    links?: InternalLink[];
    linksHeading?: string;
  } = {}
): SectionSixOverrides {

  const title =
    current?.title ??
    i18n.buildSection6DefaultTitle(keyword, locationLabel);

  const subtitle =
    current?.subtitle ??
    i18n.buildSection6DefaultSubtitle(keyword, locationLabel);

  const reviews: ReviewItem[] =
    Array.isArray(opts.reviews)
      ? opts.reviews
      : current?.reviews ?? [];

  return {
    ...current,
    title,
    subtitle,

    showAggregate:
      current?.showAggregate ??
      (opts.showAggregate ?? true),

    reviews,   // ⭐ תמיד מוגדר

    linksHeading:
      current?.linksHeading ??
      opts.linksHeading,

    links:
      current?.links ??
      opts.links,
  };
}

function buildGlobalServiceLinks(
  i18n: I18nConstants,
  type: 'חתונה' | 'חינה' | 'בר-מצווה'
): InternalLink[] {
  const isHe = i18n.HTML_LANG === 'he';
  const inRegion = (region: Region) => {
    const translatedRegion = i18n.translateRegion(region);
    return isHe ? `ב${translatedRegion}` : `in ${translatedRegion}`;
  };

  const currentLabel = i18n.preferredKeywordForService(serviceByType[type]);
  const labelForType = (eventType: 'אירוע' | 'חתונה' | 'חינה' | 'בר-מצווה') =>
    i18n.preferredKeywordForService(serviceByType[eventType]);

  return [
    ...REGIONS.map((region) => ({
      label: `${currentLabel} ${inRegion(region)}`,
      href: relUrlWithSlash(typeLocationPath(type, region)),
    })),
    ...(type === 'חתונה'
      ? []
      : [{ label: labelForType('חתונה'), href: relUrlWithSlash(globalTypePath('חתונה')) }]),
    ...(type === 'חינה'
      ? []
      : [{ label: labelForType('חינה'), href: relUrlWithSlash(globalTypePath('חינה')) }]),
    ...(type === 'בר-מצווה'
      ? []
      : [{ label: labelForType('בר-מצווה'), href: relUrlWithSlash(globalTypePath('בר-מצווה')) }]),
    {
      label: labelForType('אירוע'),
      href: relUrlWithSlash(globalTypePath('אירוע')),
    },
  ];
}

const GLOBAL_WEDDING_FAQS = [
  {
    q: 'מה כולל שירות תקליטן לחתונה?',
    a: 'השירות כולל שיחת היכרות עם הזוג, בניית קו מוזיקלי אישי, תכנון שירי קבלת הפנים, החופה, הסלואו והמסיבה, קריאת קהל בזמן אמת, מיקסים מקצועיים וליווי מוזיקלי עד סיום האירוע.',
  },
  {
    q: 'איך בונים פסקול אישי לחתונה?',
    a: 'מתחילים מהטעם של הזוג, השירים החשובים להם והאווירה שהם רוצים ליצור. משם בונים רצף שמתאים לקבלת פנים, חופה, סלואו ורחבה, עם מקום לבקשות מיוחדות ולשירים שלא תרצו שיתנגנו.',
  },
  {
    q: 'כמה עולה די ג׳יי לחתונה?',
    a: 'המחיר משתנה לפי תאריך, מיקום, גודל החתונה, שעות הנגינה והציוד הנדרש. ברוב המקרים טווח המחירים נע סביב 4,000 עד 9,000 ₪, ובאירועים מורכבים או תאריכים מבוקשים המחיר יכול להשתנות.',
  },
  {
    q: 'כמה זמן מראש כדאי לסגור תקליטן לחתונה?',
    a: 'בעונת החתונות מומלץ לסגור לפחות חצי שנה מראש, ובתאריכים מבוקשים אפילו מוקדם יותר. אם החתונה קרובה, עדיין כדאי לבדוק זמינות כי לפעמים מתפנים תאריכים.',
  },
  {
    q: 'האם אפשר לבחור את כל השירים לחתונה?',
    a: 'אפשר להעביר רשימת שירים אהובים, שירים שחייבים להיות, שירים שלא תרצו לשמוע וכיוונים כלליים. בפועל השילוב הטוב ביותר הוא בין הטעם שלכם לבין קריאת הרחבה בזמן אמת.',
  },
  {
    q: 'מה ההבדל בין דיג׳יי לחתונה לבין פלייליסט מוכן?',
    a: 'פלייליסט לא יודע לקרוא קהל, לשנות אנרגיה, לחבר בין סגנונות או להגיב לרגעים שקורים באירוע. תקליטן מנוסה מנהל את הקצב של הערב ומחזיק את הרחבה חיה לאורך זמן.',
  },
  {
    q: 'האם די ג׳יי לחתונה מגיע עם ציוד גיבוי?',
    a: 'די ג׳יי מקצועי מגיע עם תכנית גיבוי וציוד חלופי חיוני כדי לצמצם סיכונים בזמן אמת. חשוב לוודא את זה מראש כחלק מתיאום הציפיות לפני האירוע.',
  },
  {
    q: 'איך בוחרים תקליטן לחתונה שמתאים לנו?',
    a: 'כדאי לבדוק ניסיון אמיתי בחתונות, חיבור אישי, המלצות, סרטונים מאירועים, יכולת להבין את הסגנון שלכם וגישה מקצועית לתכנון הערב. החיבור עם הדיג׳יי חשוב כמעט כמו המוזיקה עצמה.',
  },
];

const GLOBAL_HENNA_FAQS = [
  {
    q: 'מה כולל שירות תקליטן לחינה?',
    a: 'השירות כולל תכנון מוזיקלי לחינה, שירי כניסה, ליווי לטקס החינה, מוזיקה למעגלים ולרחבה, קריאת קהל בזמן אמת ושילוב נכון בין מסורת, מוזיקה מזרחית ולהיטים עכשוויים.',
  },
  {
    q: 'איך מתכננים מוזיקה לחינה מרוקאית, תימנית או מעורבת?',
    a: 'מתחילים מהמסורת של המשפחה ומהסגנון שאתם אוהבים. אפשר לבנות ערב עם מקצבים מסורתיים, שירים עדתיים ומוזיקה עדכנית, כך שכל המשפחה תרגיש חלק מהחגיגה.',
  },
  {
    q: 'כמה עולה די ג׳יי לחינה?',
    a: 'המחיר משתנה לפי תאריך, מיקום, כמות אורחים, שעות נגינה ותוספות כמו תאורה, נגנים או עמדת דיג׳יי מעוצבת. בדרך כלל טווח המחירים נע סביב 2,500 עד 5,000 ₪.',
  },
  {
    q: 'האם אפשר לשלב נגנים חיים בחינה?',
    a: 'כן. אפשר לשלב שופרות, סקסופון, כנרית או בוזוקי, לפי הסגנון של החינה והתקציב. השילוב נעשה בתיאום מראש כדי שהכניסות והטקס ירגישו טבעיים.',
  },
  {
    q: 'איך שומרים על אנרגיה גבוהה בחינה?',
    a: 'הסוד הוא תכנון נכון של הכניסות, הטקס והמעבר למסיבה. תקליטן מנוסה יודע מתי לתת מקום לרגעים המשפחתיים ומתי להעלות קצב כדי שהרחבה תישאר חיה.',
  },
  {
    q: 'כמה זמן מראש כדאי לסגור דיג׳יי לחינה?',
    a: 'כדאי לסגור כמה חודשים מראש, במיוחד בעונות עמוסות ובתאריכים מבוקשים. אם האירוע קרוב, עדיין שווה לבדוק זמינות ולתאם במהירות את הפרטים החשובים.',
  },
];

const GLOBAL_BAR_BAT_FAQS = [
  {
    q: 'מה כולל שירות תקליטן לבר מצווה או בת מצווה?',
    a: 'השירות כולל תכנון מוזיקלי לאירוע, כניסה לחוגג או לחוגגת, מוזיקה לנוער ולמשפחה, ליווי לברכות או דרשה, קריאת רחבה בזמן אמת ושמירה על אנרגיה שמתאימה לכל הגילים.',
  },
  {
    q: 'איך בונים פלייליסט לבר/בת מצווה?',
    a: 'משלבים בין השירים שהחוגג או החוגגת אוהבים לבין מוזיקה שמתאימה למשפחה ולאורחים. המטרה היא לתת לנוער להרגיש במרכז, בלי לאבד את המבוגרים ברחבה.',
  },
  {
    q: 'כמה עולה די ג׳יי לבר/בת מצווה?',
    a: 'המחיר משתנה לפי גודל האירוע, התאריך, המקום, שעות הנגינה ותוספות כמו תאורה, רקדנים או אטרקציות. בדרך כלל טווח המחירים נע סביב 2,500 עד 4,500 ₪.',
  },
  {
    q: 'איך מתכננים כניסה מרשימה לבר/בת מצווה?',
    a: 'בוחרים שיר כניסה, מתכננים טיימינג, מיקום והכרזה, ולעיתים משלבים אפקטים או רקדנים. הכל צריך להתאים לאופי של החוגג או החוגגת ולא להרגיש מוגזם.',
  },
  {
    q: 'איך שומרים על רחבה שמתאימה גם לנוער וגם למבוגרים?',
    a: 'תקליטן טוב יודע לעבור בין להיטים עדכניים, שירי מסיבה מוכרים וקלאסיקות שכל המשפחה מתחברת אליהן. קריאת קהל נכונה שומרת על הרחבה פעילה לאורך הערב.',
  },
  {
    q: 'האם אפשר לשלב משחקים, רקדנים או הפעלות?',
    a: 'כן, אפשר לשלב רקדנים, מנחה או הפעלות רחבה, כל עוד זה מתוכנן מראש ומשתלב בקצב של האירוע. חשוב שההפעלות יחזקו את המסיבה ולא יעצרו אותה.',
  },
];

function getGlobalFaqsForService(service: SeoService) {
  if (service === SEO_SERVICE_WEDDING) return GLOBAL_WEDDING_FAQS;
  if (service === SEO_SERVICE_HENNA) return GLOBAL_HENNA_FAQS;
  if (service === SEO_SERVICE_BAR_BAT) return GLOBAL_BAR_BAT_FAQS;
  return undefined;
}

function getGlobalLinksForService(i18n: I18nConstants, service: SeoService) {
  if (service === SEO_SERVICE_WEDDING) return buildGlobalServiceLinks(i18n, 'חתונה');
  if (service === SEO_SERVICE_HENNA) return buildGlobalServiceLinks(i18n, 'חינה');
  if (service === SEO_SERVICE_BAR_BAT) return buildGlobalServiceLinks(i18n, 'בר-מצווה');
  return undefined;
}
// ============================================================================
// Base builder (shared skeleton)
// ============================================================================
function makeBase(
  i18n: I18nConstants,
  service: SeoService,
  keyword: string,
  locationLabel: string,
  opts: BuildOverridesOptions = {},
  faqs?: any
): PageOverrides {
  const loc = locationLabel.trim();
  const isGlobal = !loc;
  const cleanKeyword = String(keyword ?? '').trim();

  const subtitleFn = ({ isDesktop }: { isDesktop: boolean }) => {
    const city = isGlobal ? '' : loc;

    const base = isDesktop
      ? EVENT_TYPE_DESCRIPTION_DESKTOP(i18n, service, city as any)
      : EVENT_TYPE_DESCRIPTION(i18n, service, city as any);

    return [base, opts.extraSubtitle].filter(Boolean).join('\n');
  };

  const titleSuffix = isGlobal ? '' : locSuffix(i18n, loc);
  const targetKeyword = `${cleanKeyword}${titleSuffix}`.trim();
  const section1Title =
    i18n.HTML_LANG === 'he' && service === SEO_SERVICE_EVENT && !isGlobal
      ? `די ג׳יי לאירוע ${withBetNoLeadSpace(loc as any)}`
      : targetKeyword;

  return {
    section1: {
      title: section1Title,
      descriptionLines:
        i18n.buildSection1TargetDescriptionLines(
          service,
          cleanKeyword,
          isGlobal ? '' : loc
        ),
      imageAlt: `${targetKeyword} – ${i18n.BRAND_TITLE_SHORT}`,
    },

    section2: {
      title: i18n.SECTION_TWO_TITLE,
      description: i18n.buildSection2TargetDescription(
        i18n.SECTION_TWO_DESCRIPTION,
        cleanKeyword,
        isGlobal ? '' : loc
      ),
      additionalText: i18n.buildSection2TargetAdditionalText(
        service,
        i18n.SECTION_TWO_ADDITIONAL_TEXT,
        isGlobal ? '' : loc
      ),
      portraitAlt: `${targetKeyword} – ${i18n.SECTION_TWO_IMG_ALT}`,
      horizontalAlt: `${targetKeyword} – ${i18n.SECTION_TWO_HORIZONTAL_IMG_ALT}`,
    },

    section3: {
      defaultAlbum:
        i18n.SECTION_THREE_DEFAULT_ALBUM_BY_SERVICE[service] ??
        SECTION_THREE_DEFAULT_ALBUM,
    },

    section4: buildSection4Block(
      i18n,
      service,
      cleanKeyword,
      isGlobal ? '' : loc
    ),

    section5: {
      title: isGlobal
        ? `${i18n.SECTION_FIVE_TITLE}\n${cleanKeyword}`
        : `${i18n.SECTION_FIVE_TITLE}\n${targetKeyword}`,
      subtitle: subtitleFn,
      faqs,
      extraImage: {
        src: SECTION_FIVE_DEFAULT_IMG,
        alt: `${targetKeyword} – רגעים מהרחבה`,
      },
      linksHeading:
        opts.section5LinksHeading ??
        i18n.SECTION_FIVE_LINKS_HEADING,
      links: opts.section5Links,
    },

    section6: buildSection6(
      i18n,
      cleanKeyword,
      isGlobal ? '' : loc,
      undefined,
      {
        reviews: opts.section6Reviews,
        showAggregate: opts.section6ShowAggregate,
        linksHeading: opts.section6LinksHeading,
        links: opts.section6Links,
      }
    ),

    section7: buildPlanningSectionForService(service, i18n, {
      city: isGlobal ? undefined : loc,
    }),
  };
}
// ============================================================================
// Tweaks per kind
// ============================================================================
function tweaksFor(
  i18n: I18nConstants,
  kind: OverrideKind,
  opts: BuildOverridesOptions
): DeepPartial<PageOverrides> {
  switch (kind.kind) {
    case 'target': {
      const { service, keyword, locationLabel } = kind;
      const loc = normalizeLabel(locationLabel);
      const cleanKeyword = String(keyword ?? '').trim();
      const isHe = i18n.HTML_LANG === 'he';
      const globalFaqs = !loc && isHe ? getGlobalFaqsForService(service) : undefined;
      const globalLinks = !loc ? getGlobalLinksForService(i18n, service) : undefined;
      const isGlobalParent = Boolean(globalFaqs || globalLinks);

      const cityish = asSeoCityUnsafe(loc ? loc : '');

      return {
        section3: {
          extraHeadings: buildGalleryHeadings(
            i18n,
            service,
            cleanKeyword,
            cityish
          ),
        },

        section5: {
          h3Headings: buildSection5Headings(
            i18n,
            service,
            cleanKeyword,
            cityish
          ),

          faqs: opts.faqsOverride ?? (globalFaqs ?? i18n.buildFaq(service, cityish)),
          linksHeading: isGlobalParent
            ? isHe
              ? 'עמודים קשורים לפי סוג האירוע והאזור'
              : 'Related pages by event type and area'
            : undefined,
          links: globalLinks,
        },
      };
    }

    case 'home': {
      const service: SeoService = SEO_SERVICE_EVENT;
      const keyword = 'די ג׳יי';
      const loc = 'כל רחבי הארץ';

      return {
        section1: {
          title: i18n.HOME_HERO_TITLE,
          descriptionLines: i18n.HOME_SECTION1_DESCRIPTION_LINES,
          imageAlt: `${service} – ${i18n.BRAND_TITLE_SHORT}`,
        },

        section2: {
          description: `${i18n.SECTION_TWO_DESCRIPTION} ${i18n.HOME_SECTION2_DESCRIPTION_SUFFIX}`,

          additionalText: `${i18n.SECTION_TWO_ADDITIONAL_TEXT} ${i18n.HOME_SECTION2_ADDITIONAL_SUFFIX}`,

          portraitAlt: `${service} – ${i18n.SECTION_TWO_IMG_ALT}`,
          horizontalAlt: `${service} – ${i18n.SECTION_TWO_HORIZONTAL_IMG_ALT}`,
        },

        section3: {
          extraHeadings: buildGalleryHeadings(
            i18n,
            service,
            'די ג׳יי לאירוע',
            loc as any
          ),
        },

        section4: buildSection4Block(
          i18n,
          service,
          'די ג׳יי',
          ''
        ),

        section5: {
          title: i18n.SECTION_FIVE_TITLE,

          subtitle: ({ isDesktop }: { isDesktop: boolean }) =>
            isDesktop
              ? i18n.HOME_SECTION5_SUBTITLE_DESKTOP
              : i18n.HOME_SECTION5_SUBTITLE_MOBILE,

          h3Headings: buildSection5Headings(
            i18n,
            service,
            'די ג׳יי לאירוע',
            '' as any
          ),

          faqs: i18n.buildFaq(SEO_SERVICE_EVENT, '') 
        },

        section6: buildSection6(
          i18n,
          keyword,
          loc,
          {
            title: i18n.HOME_SECTION6_TITLE,
            subtitle: i18n.HOME_SECTION6_SUBTITLE,
            reviews: [],
          },
          {
            reviews: opts.section6Reviews,
            showAggregate:
              opts.section6ShowAggregate ?? true,
          }
        ),
      };
    }
  }
}

// ============================================================================
// Public API
// ============================================================================
export function buildOverrides(
  kind: OverrideKind,
  i18n: I18nConstants,
  opts: BuildOverridesOptions = {}
): PageOverrides {
  if (kind.kind === 'target') {
    const base = makeBase(
      i18n,
      kind.service,
      kind.keyword,
      kind.locationLabel,
      opts,
      opts.faqsOverride
    );

    return deepMerge(base, tweaksFor(i18n, kind, opts));
  }

  // home
  const service: SeoService = SEO_SERVICE_EVENT;

  const base = makeBase(
    i18n,
    service,
    service,
    'כל רחבי הארץ',
    opts,
    opts.faqsOverride
  );

  return deepMerge(
    base,
    tweaksFor(i18n, { kind: 'home' }, opts)
  );
}

// Convenience wrappers (keep old call-sites stable)
export const buildTargetOverrides = (
  service: SeoService,
  keyword: string,
  cityOrRegionOrEmpty: string,
  i18n: I18nConstants,
  opts?: BuildOverridesOptions
) =>
  buildOverrides(
    {
      kind: 'target',
      service,
      keyword,
      locationLabel: cityOrRegionOrEmpty,
    },
    i18n,
    opts
  );

export const buildHomeOverrides = (
  i18n: I18nConstants,
  opts?: BuildOverridesOptions
) =>
  buildOverrides(
    { kind: 'home' },
    i18n,
    opts
  );
